// Laser "Add Sheet & Track Material" for REMNANT pieces (2026-09-21).
//
// A remnant (a leftover piece the laser auto-remnant put back into stock) has no tracking
// number of its own; its printed tag carries the lot of the sheet it was cut from. jobq-api
// now accepts that lot for a remnant and records it as a REMNANT CUT: the lot's heat, PO and
// supplier trace the parts, but the piece never counts as one of the lot's sheets.
//
//   GET /laser/{task_id}/tracking_required   -> source "remnant", candidate_mtns (lots with a
//                                               piece of this remnant on record)
//   GET /material_tracking/{id}/sheet_context?task_id= -> cut_kind "sheet" | "remnant" | "mismatch"
//
// Since 2026-10-14 a remnant HAS a lot of its own (source_type "remnant", parent = the lot it was
// cut from): candidate_mtns lists those first (own_lot true), the tag prints that TRK, and an OLD
// tag (the parent lot) is recorded on the remnant's own lot (sheet_context recorded_as_mtn_id).

const pad6 = (n) => String(n).padStart(6, "0");

/**
 * Tracking number to pre-fill for a remnant task: only when exactly one lot is on record AND
 * every piece of the remnant is accounted for (unrecorded_pieces 0). With unrecorded pieces the
 * piece in hand may carry a different lot on its tag, and a pre-filled number invites the
 * operator to confirm the wrong heat.
 */
export function defaultRemnantTrackingNumber(info) {
  if (!info || info.source !== "remnant") return "";
  if (Number(info.unrecorded_pieces || 0) > 0) return "";
  const lots = Array.isArray(info.candidate_mtns) ? info.candidate_mtns : [];
  return lots.length === 1 && lots[0].mtn_id ? String(lots[0].mtn_id) : "";
}

/** A refusal the operator may override (the API ends every such message this way). */
export function isConfirmableSheetRefusal(message) {
  return /already fully used|confirm to record it anyway/i.test(message || "");
}

/** "TRK 000401 · heat A606832 · 1 piece" for a candidate lot button. */
export function remnantLotLabel(lot) {
  if (!lot) return "";
  const bits = [`TRK ${lot.tracking_number_display || pad6(lot.mtn_id)}`];
  if (lot.heat_number) bits.push(`heat ${lot.heat_number}`);
  const n = Number(lot.pieces_remaining || 0);
  bits.push(`${n} piece${n === 1 ? "" : "s"}`);
  // A lot the remnant was cut FROM (an old tag, before remnants had lots of their own).
  if (lot.own_lot === false) bits.push("sheet lot");
  return bits.join(" · ");
}

/** The sheet(s) a remnant descends from, nearest first, as one readable phrase. */
export function remnantOfText(info) {
  const chain = (info && Array.isArray(info.remnant_of)) ? info.remnant_of : [];
  const root = chain.length ? chain[chain.length - 1] : null;
  return root && root.material_code ? root.material_code : "";
}

/**
 * How the entered tag reads for this task.
 *   tone "sheet"   - a sheet of the lot: "Sheet #N of this lot · R remaining" (unchanged)
 *   tone "remnant" - a remnant cut from the lot (no sheet number: it is not a lot sheet)
 *   tone "warn"    - remnant, but no piece of it from this lot is on record (likely wrong tag)
 *   tone "error"   - the tag is for a different material; the save will be refused
 */
export function describeTagContext(ctx) {
  if (!ctx) return null;
  if (ctx.cut_kind === "mismatch") {
    return {
      tone: "error",
      text: `Wrong material: this tag is ${ctx.material_code || "another material"}, `
        + `but this job cuts ${ctx.task_material_code || "a different material"}.`,
    };
  }
  if (ctx.cut_kind === "remnant") {
    const heat = ctx.heat_number ? ` · heat ${ctx.heat_number}` : "";
    const left = (n) => `${n} piece${n === 1 ? "" : "s"} left`;
    if (ctx.recorded_as_mtn_id) {
      const n = Number(ctx.remnant_pieces_remaining || 0);
      return {
        tone: "remnant",
        text: `Old tag: recorded on this remnant's own lot TRK ${pad6(ctx.recorded_as_mtn_id)}${heat} · ${left(n)}`,
      };
    }
    if (ctx.material_code && ctx.material_code === ctx.task_material_code) {
      const n = Number(ctx.remnant_pieces_remaining || 0);
      return n > 0
        ? { tone: "remnant", text: `This remnant's own lot${heat} · ${left(n)}` }
        : { tone: "warn", text: `This remnant's own lot${heat} · no piece left on record. Check the tag.` };
    }
    if (ctx.lot_sheets_cut === 0) {
      return {
        tone: "warn",
        text: `No sheet of this lot has been cut${heat}, so this remnant cannot be from it. Check the tag.`,
      };
    }
    if (typeof ctx.remnant_pieces_remaining === "number") {
      const n = ctx.remnant_pieces_remaining;
      if (n <= 0) {
        return {
          tone: "warn",
          text: `Remnant cut from this lot${heat} · no piece of this remnant from this lot is on record. Check the tag.`,
        };
      }
      return {
        tone: "remnant",
        text: `Remnant cut from this lot${heat} · ${n} piece${n === 1 ? "" : "s"} of this remnant on record`,
      };
    }
    return { tone: "remnant", text: `Remnant cut from this lot${heat}` };
  }
  const rem = typeof ctx.remaining_qty === "number" ? `  ·  ${ctx.remaining_qty} remaining` : "";
  return { tone: "sheet", text: `Sheet #${ctx.next_sheet_number} of this lot${rem}` };
}

/** Split an MTN's consumption rows into the lot's own sheets and remnant cuts. */
export function splitLotUsage(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const remnantCuts = list.filter((u) => u && u.is_remnant_cut).length;
  return { sheets: list.length - remnantCuts, remnantCuts };
}

/**
 * "No Tracking Info Available" needs a manager (jobq-api _no_tracking_block, 2026-10-20): every sheet / piece
 * of this material in stock is on a tracking number, so the one in hand has one too. The API answers
 * no_tracking_needs_manager + no_tracking_lots + no_tracking_message on GET /laser/{id}/tracking_required,
 * and refuses Add Sheet without the manager's PIN and a reason.
 */
export function noTrackingNeedsManager(info) {
  return !!(info && info.no_tracking_needs_manager);
}

/** The API's refusal of a sheet with no lot (increment_sheet 400 detail {error: 'no_tracking_needs_manager',
 *  message, lots}) from the response text, or null for any other answer. */
export function noTrackingRefusal(text) {
  try {
    const body = typeof text === "string" ? JSON.parse(text) : text;
    const d = body && body.detail;
    if (d && typeof d === "object" && d.error === "no_tracking_needs_manager") {
      return { message: d.message || "Every sheet of this material in stock is on a tracking number.",
               lots: Array.isArray(d.lots) ? d.lots : [] };
    }
  } catch (_) { /* not JSON */ }
  return null;
}
/** The manager override's fields, or an error to show: a PIN and a reason of at least 5 characters. */
export function noTrackingOverride(managerPin, reason) {
  const pin = String(managerPin || "").trim();
  const why = String(reason || "").trim();
  if (!pin) return { error: "A manager PIN is required to record a sheet with no tracking number." };
  if (why.length < 5) return { error: "Say why there is no tracking number (at least 5 characters)." };
  return { managerPin: pin, managerReason: why };
}
